/* eslint-disable no-param-reassign */
import * as utils from '../../../../util';
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicBeeSwarmLayerSpec } from '../../../../layer/preset/scatter/dynamicBeeSwarm';
import { getDynamicBeeSwarmAdapters } from './dynamicBeeSwarmAdapters';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface DynamicBeeSwarmLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type DynamicBeeSwarmLayerType = StandardChartLayerSpec &
  DynamicBeeSwarmLayerSpec &
  DynamicBeeSwarmLayerTypeWithFields;

// 创建动态蜂群图数据类型验证器（验证新版本编码格式）
const validateDynamicBeeSwarmDataTypes = createDataTypeValidator('dynamicBeeSwarm', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' },
  { field: 'group', type: ['string', 'date'] },
  { field: 'size', type: 'number', required: false }
]);

// 创建动态蜂群图编码验证器（验证新版本编码格式）
const validateDynamicBeeSwarmEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'group' },
  { field: 'size', required: false }
]);

// 额外的检查函数 - 验证每个分组内名称的唯一性（使用新版本编码）
const additionalDynamicBeeSwarmChecks = (layer: DynamicBeeSwarmLayerType, data: Data) => {
  const { dataIndex = 0, encoding } = layer;
  const rawData = data[dataIndex]?.values || [];

  if (!rawData.length) {
    return;
  }

  // 使用新版本的group字段作为分组字段
  const groupField = encoding.group as string;
  if (!groupField || typeof groupField !== 'string') {
    return;
  }

  // 获取不同的分组值
  const datesSortList = (utils.dataTransform.transform(rawData.slice(), [
    { type: 'deDuplication', field: groupField },
    { type: 'sort', field: groupField, order: 'ascending' }
  ]) || []) as { [key: string]: string | number }[];

  const { dates } = utils.dataEncoding.encoding(datesSortList, {
    dates: { field: groupField }
  });

  // 检查每个分组内的名称是否唯一
  dates.forEach(date => {
    const dateData = rawData.filter(item => item[groupField] === date);
    const nameList = dateData.map(item => item[encoding.x as string]);
    if (nameList.length !== new Set(nameList).size) {
      utils.logger.warn(
        'warning',
        `The name of the corresponding "${encoding.x}" field in the data of each "${date}" period in the "${layer.type}" cannot be repeated`
      );
      layer.__isEncodingValid = false;
    }
  });
};

// 创建并导出动态蜂群图处理器，集成编码适配器
export const dynamicBeeSwarm = createLayerProcessor<DynamicBeeSwarmLayerType>(
  'dynamicBeeSwarm',
  validateDynamicBeeSwarmEncoding,
  validateDynamicBeeSwarmDataTypes,
  additionalDynamicBeeSwarmChecks,
  getDynamicBeeSwarmAdapters()
);
